import React, { useState, useEffect } from "react";
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import brand from "../config/brand";

// GuestPortal.jsx — S9
// AS-116: Public-facing — no login required
// Accessed via /guest?b=BOOKING_ID
// Steps: (1) identity confirmation → (2) waiver sign → (3) access code display
// waiverSignedAt written to booking doc on sign — triggers issueAccessCode eligibility

const WAIVER_TEXT = `SHORT-TERM RENTAL GUEST AGREEMENT

By signing below, you agree to the following terms and conditions for your stay:

1. OCCUPANCY: The property is reserved exclusively for the registered guests. Unauthorized guests or parties are strictly prohibited and may result in immediate termination of your stay without refund.

2. NOISE & NEIGHBORS: Quiet hours are 10:00 PM to 8:00 AM. Please be respectful of neighbors at all times.

3. NO SMOKING: Smoking of any substance is strictly prohibited inside the property. A deep-cleaning fee of $250 will be charged for violations.

4. PETS: No pets are permitted unless explicitly authorized in your booking confirmation.

5. PROPERTY CARE: You are responsible for leaving the property in the same condition as you found it. Damage beyond normal wear will be charged to the payment method on file.

6. ACCESS CODE: Your access code is provided for your personal use only. Do not share it with unauthorized individuals.

7. CHECK-OUT: Please check out by the stated time. Late departures may incur additional charges.

8. LIABILITY: The property manager is not liable for personal injury, theft, or loss of personal belongings during your stay.

9. GOVERNING LAW: This agreement is governed by the laws of the state in which the property is located.

By signing below, you confirm that you have read, understood, and agree to all terms above.`;

function useBookingId() {
  const params = new URLSearchParams(window.location.search);
  return params.get("b");
}

export default function GuestPortal() {
  const db = getFirestore();
  const bookingId = useBookingId();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step,    setStep]    = useState(1); // 1=confirm, 2=waiver, 3=code

  const [confirmName,  setConfirmName]  = useState("");
  const [confirmError, setConfirmError] = useState("");

  const [waiverScrolled, setWaiverScrolled]   = useState(false);
  const [waiverChecked,  setWaiverChecked]     = useState(false);
  const [signatureName,  setSignatureName]     = useState("");
  const [signing,        setSigning]           = useState(false);
  const [signError,      setSignError]         = useState("");

  useEffect(() => {
    if (!bookingId) { setLoading(false); return; }
    getDoc(doc(db, "bookings", bookingId)).then((snap) => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setBooking(data);
        // If already signed, skip to code
        if (data.waiverSignedAt) setStep(3);
      }
      setLoading(false);
    });
  }, [bookingId, db]);

  function handleConfirm() {
    setConfirmError("");
    if (!confirmName.trim()) { setConfirmError("Please enter your name."); return; }
    const match = booking.guestName?.toLowerCase().trim();
    const input = confirmName.toLowerCase().trim();
    // Partial match allowed — first name OR full name
    if (match && !match.includes(input.split(" ")[0])) {
      setConfirmError("Name doesn't match the booking. Please use the name on your reservation.");
      return;
    }
    setStep(2);
  }

  async function handleSign() {
    setSignError("");
    if (!waiverChecked) { setSignError("Please check the acknowledgment box."); return; }
    if (!signatureName.trim()) { setSignError("Please type your full name as your signature."); return; }
    setSigning(true);
    try {
      await updateDoc(doc(db, "bookings", bookingId), {
        waiverSignedAt:    serverTimestamp(),
        waiverSignedName:  signatureName.trim(),
        status:            "waiver_signed",
        updatedAt:         serverTimestamp(),
      });
      setStep(3);
      // Reload booking to get access code if already issued
      const snap = await getDoc(doc(db, "bookings", bookingId));
      if (snap.exists()) setBooking({ id: snap.id, ...snap.data() });
    } catch (err) {
      setSignError("Failed to save signature. Please try again.");
    } finally {
      setSigning(false);
    }
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={portalStyles.page}>
        <div style={portalStyles.card}>
          <div style={portalStyles.loadingWrap}>
            <div style={portalStyles.spinner} />
            <span style={portalStyles.loadingText}>Loading your check-in…</span>
          </div>
        </div>
      </div>
    );
  }

  if (!bookingId || !booking) {
    return (
      <div style={portalStyles.page}>
        <div style={portalStyles.card}>
          <div style={portalStyles.errorPage}>
            <span style={portalStyles.errorIcon}>🔍</span>
            <h2 style={portalStyles.errorTitle}>Booking not found</h2>
            <p style={portalStyles.errorSub}>This link may be invalid or expired. Contact your host for assistance.</p>
          </div>
        </div>
      </div>
    );
  }

  const ci = booking.checkIn?.toDate  ? booking.checkIn.toDate()  : booking.checkIn  ? new Date(booking.checkIn)  : null;
  const co = booking.checkOut?.toDate ? booking.checkOut.toDate() : booking.checkOut ? new Date(booking.checkOut) : null;
  const fmtDate = (d) => d ? d.toLocaleDateString("en-US",{ weekday:"short", month:"long", day:"numeric" }) : "—";

  return (
    <div style={portalStyles.page}>
      {/* Header */}
      <div style={portalStyles.header}>
        <span style={portalStyles.wordmarkCyan}>vertical</span>
        <span style={portalStyles.wordmarkWhite}>stack</span>
        <span style={portalStyles.headerSub}>Guest Check-In</span>
      </div>

      <div style={portalStyles.card}>
        {/* Stay summary */}
        <div style={portalStyles.stayBanner}>
          <div style={portalStyles.stayDates}>
            <div style={portalStyles.stayDate}>
              <span style={portalStyles.dateLabelSmall}>CHECK-IN</span>
              <span style={portalStyles.dateValue}>{fmtDate(ci)}</span>
            </div>
            <span style={portalStyles.dateSep}>→</span>
            <div style={portalStyles.stayDate}>
              <span style={portalStyles.dateLabelSmall}>CHECK-OUT</span>
              <span style={portalStyles.dateValue}>{fmtDate(co)}</span>
            </div>
          </div>
        </div>

        {/* Step indicator */}
        <div style={portalStyles.stepRow}>
          {["Confirm Identity", "Sign Waiver", "Access Code"].map((label, i) => (
            <React.Fragment key={i}>
              <div style={portalStyles.stepItem}>
                <div style={{
                  ...portalStyles.stepDot,
                  ...(i + 1 < step ? portalStyles.stepDone : {}),
                  ...(i + 1 === step ? portalStyles.stepActive : {}),
                }}>
                  {i + 1 < step ? "✓" : i + 1}
                </div>
                <span style={{
                  ...portalStyles.stepLabel,
                  ...(i + 1 === step ? portalStyles.stepLabelActive : {}),
                }}>{label}</span>
              </div>
              {i < 2 && (
                <div style={{
                  ...portalStyles.stepLine,
                  ...(i + 1 < step ? portalStyles.stepLineDone : {}),
                }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* ── Step 1: Confirm Identity ───────────────────────────────── */}
        {step === 1 && (
          <div style={portalStyles.stepBody}>
            <h2 style={portalStyles.stepTitle}>Welcome! Let's confirm your identity.</h2>
            <p style={portalStyles.stepSub}>
              Enter the name on your reservation to proceed to digital check-in.
            </p>
            <label style={portalStyles.label}>Your Name</label>
            <input
              style={portalStyles.input}
              placeholder="First and last name"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
              autoFocus
            />
            {confirmError && <p style={portalStyles.errorMsg}>{confirmError}</p>}
            <button style={portalStyles.btnPrimary} onClick={handleConfirm}>
              Continue →
            </button>
          </div>
        )}

        {/* ── Step 2: Waiver ────────────────────────────────────────── */}
        {step === 2 && (
          <div style={portalStyles.stepBody}>
            <h2 style={portalStyles.stepTitle}>Guest Rental Agreement</h2>
            <p style={portalStyles.stepSub}>
              Please read the agreement below, then sign to proceed.
            </p>
            <div
              style={portalStyles.waiverBox}
              onScroll={(e) => {
                const el = e.target;
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
                  setWaiverScrolled(true);
                }
              }}
            >
              {WAIVER_TEXT.split("\n\n").map((para, i) => (
                <p key={i} style={portalStyles.waiverPara}>{para}</p>
              ))}
              {!waiverScrolled && (
                <div style={portalStyles.scrollHint}>↓ Scroll to read full agreement</div>
              )}
            </div>

            <div style={portalStyles.checkRow}>
              <input
                type="checkbox"
                id="waiver-ack"
                checked={waiverChecked}
                onChange={(e) => setWaiverChecked(e.target.checked)}
                style={{ width: 16, height: 16, cursor: "pointer" }}
              />
              <label htmlFor="waiver-ack" style={portalStyles.checkLabel}>
                I have read and agree to the Guest Rental Agreement above
              </label>
            </div>

            <label style={portalStyles.label}>Type your full name as your digital signature</label>
            <input
              style={portalStyles.input}
              placeholder="Full legal name"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
            />

            {signError && <p style={portalStyles.errorMsg}>{signError}</p>}

            <button
              style={{
                ...portalStyles.btnPrimary,
                ...(!waiverChecked || !signatureName.trim() ? portalStyles.btnDisabled : {}),
              }}
              onClick={handleSign}
              disabled={signing || !waiverChecked || !signatureName.trim()}
            >
              {signing ? "Signing…" : "Sign & Continue →"}
            </button>
          </div>
        )}

        {/* ── Step 3: Access Code ───────────────────────────────────── */}
        {step === 3 && (
          <div style={portalStyles.stepBody}>
            <div style={portalStyles.successIcon}>✓</div>
            <h2 style={portalStyles.stepTitle}>Check-in complete!</h2>

            {booking.accessCode ? (
              <>
                <p style={portalStyles.stepSub}>Your access code for this stay:</p>
                <div style={portalStyles.codeBox}>
                  <span style={portalStyles.codeValue}>{booking.accessCode}</span>
                  <span style={portalStyles.codeDates}>
                    Valid {fmtDate(ci)} – {fmtDate(co)}
                  </span>
                </div>
                <p style={portalStyles.codeSmsNote}>
                  This code was also sent to your phone via SMS.
                </p>
              </>
            ) : (
              <>
                <p style={portalStyles.stepSub}>
                  Your waiver has been signed. Your access code will be sent to your phone
                  shortly before your check-in date.
                </p>
                <div style={portalStyles.pendingCodeBox}>
                  <span style={portalStyles.pendingCodeIcon}>⏳</span>
                  <span>Access code pending issuance by host</span>
                </div>
              </>
            )}

            <div style={portalStyles.helpNote}>
              Need help? Contact your host directly. Have a great stay!
            </div>
          </div>
        )}
      </div>

      <div style={portalStyles.footer}>
        Powered by <strong>verticalstack</strong> · stack more, manage less
      </div>
    </div>
  );
}

const portalStyles = {
  page: {
    minHeight: "100vh",
    background: "#0D1B2A",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0 16px 40px",
  },
  header: {
    padding: "24px 0 20px",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  wordmarkCyan: {
    fontFamily: "Arial, sans-serif", fontWeight: 800, fontSize: 22,
    color: brand.colors.cyan,
  },
  wordmarkWhite: {
    fontFamily: "Arial, sans-serif", fontWeight: 800, fontSize: 22,
    color: "white",
  },
  headerSub: {
    fontSize: 12, color: "rgba(255,255,255,0.45)", marginLeft: 8,
    borderLeft: "1px solid rgba(255,255,255,0.2)", paddingLeft: 8,
  },
  card: {
    background: "white", borderRadius: 16, width: "100%", maxWidth: 520,
    overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
  },
  stayBanner: {
    background: brand.colors.navy, padding: "16px 24px",
  },
  stayDates: {
    display: "flex", alignItems: "center", gap: 12,
  },
  stayDate: { display: "flex", flexDirection: "column", gap: 2 },
  dateLabelSmall: { fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.6)", letterSpacing: "0.8px" },
  dateValue: { fontSize: 14, fontWeight: 700, color: "white" },
  dateSep: { fontSize: 18, color: "rgba(255,255,255,0.4)", margin: "0 4px" },

  // Step indicator
  stepRow: {
    display: "flex", alignItems: "center", padding: "16px 24px",
    borderBottom: `1px solid ${brand.colors.border}`,
  },
  stepItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 },
  stepDot: {
    width: 24, height: 24, borderRadius: "50%", background: brand.colors.pageBg,
    color: brand.colors.textMuted, fontSize: 11, fontWeight: 700,
    display: "flex", alignItems: "center", justifyContent: "center",
    border: `1px solid ${brand.colors.border}`,
  },
  stepActive: { background: brand.colors.navy, color: "white", border: `1px solid ${brand.colors.navy}` },
  stepDone:   { background: brand.colors.cyan,  color: "white", border: `1px solid ${brand.colors.cyan}` },
  stepLine: { flex: 1, height: 2, background: brand.colors.border, margin: "0 6px", marginBottom: 16 },
  stepLineDone: { background: brand.colors.cyan },
  stepLabel: { fontSize: 10, color: brand.colors.textMuted, fontWeight: 500, whiteSpace: "nowrap" },
  stepLabelActive: { color: brand.colors.navy, fontWeight: 700 },

  // Body
  stepBody: {
    padding: "24px 24px 28px",
    display: "flex", flexDirection: "column", gap: 14,
  },
  stepTitle: {
    fontFamily: "Arial, sans-serif", fontSize: 18, fontWeight: 700,
    color: brand.colors.navy, margin: 0,
  },
  stepSub: { fontSize: 14, color: brand.colors.textMuted, margin: 0, lineHeight: 1.6 },
  label: { fontSize: 13, fontWeight: 600, color: brand.colors.text },
  input: {
    padding: "12px 14px", border: `1px solid ${brand.colors.border}`, borderRadius: 8,
    fontSize: 14, color: brand.colors.text, background: brand.colors.pageBg,
    outline: "none", fontFamily: "Arial, sans-serif",
  },
  btnPrimary: {
    padding: "13px 0", background: brand.colors.navy, color: "white",
    border: "none", borderRadius: 8, fontSize: 15, fontWeight: 700,
    cursor: "pointer", fontFamily: "Arial, sans-serif",
  },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  errorMsg: {
    fontSize: 13, color: "#dc2626", padding: "8px 12px", background: "#fef2f2",
    borderRadius: 6, borderLeft: "3px solid #dc2626", margin: 0,
  },

  // Waiver
  waiverBox: {
    height: 240, overflowY: "auto", border: `1px solid ${brand.colors.border}`,
    borderRadius: 8, padding: "14px 16px", background: brand.colors.pageBg,
    position: "relative",
  },
  waiverPara: { fontSize: 12, color: brand.colors.text, lineHeight: 1.7, margin: "0 0 12px" },
  scrollHint: {
    position: "sticky", bottom: 0, textAlign: "center",
    fontSize: 11, color: brand.colors.textMuted, padding: "4px 0",
    background: "linear-gradient(transparent, #f8f9fa)",
  },
  checkRow: { display: "flex", gap: 10, alignItems: "flex-start" },
  checkLabel: { fontSize: 13, color: brand.colors.text, lineHeight: 1.5, cursor: "pointer" },

  // Access code
  successIcon: {
    width: 52, height: 52, borderRadius: "50%", background: brand.colors.cyan,
    color: "white", fontSize: 24, fontWeight: 700,
    display: "flex", alignItems: "center", justifyContent: "center",
    margin: "0 auto",
  },
  codeBox: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
    padding: "20px", background: brand.colors.pageBg, borderRadius: 12,
    border: `2px solid ${brand.colors.cyan}`,
  },
  codeValue: {
    fontSize: 44, fontWeight: 900, letterSpacing: "0.25em",
    color: brand.colors.navy, fontFamily: "monospace",
  },
  codeDates: { fontSize: 12, color: brand.colors.textMuted },
  codeSmsNote: { fontSize: 12, color: "#16a34a", textAlign: "center", margin: 0 },
  pendingCodeBox: {
    display: "flex", gap: 10, alignItems: "center", padding: "12px 16px",
    background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8,
    fontSize: 13, color: "#92400e",
  },
  pendingCodeIcon: { fontSize: 18 },
  helpNote: {
    fontSize: 13, color: brand.colors.textMuted, textAlign: "center",
    padding: "12px 0 0", borderTop: `1px solid ${brand.colors.border}`,
  },

  // Loading / error
  loadingWrap: {
    display: "flex", flexDirection: "column", alignItems: "center",
    gap: 12, padding: 48,
  },
  spinner: {
    width: 24, height: 24, border: `2px solid ${brand.colors.border}`,
    borderTopColor: brand.colors.cyan, borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  },
  loadingText: { fontSize: 14, color: brand.colors.textMuted },
  errorPage: {
    display: "flex", flexDirection: "column", alignItems: "center",
    padding: 48, gap: 12,
  },
  errorIcon: { fontSize: 36 },
  errorTitle: {
    fontFamily: "Arial, sans-serif", fontSize: 20, fontWeight: 700,
    color: brand.colors.navy, margin: 0,
  },
  errorSub: { fontSize: 14, color: brand.colors.textMuted, textAlign: "center", margin: 0 },

  footer: {
    marginTop: 20, fontSize: 12, color: "rgba(255,255,255,0.3)",
  },
};
